import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, History, X } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const fmt = (d) => format(parseISO(d), "d MMM yyyy");
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function MyLeave() {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(() => {
    api.get("/leave/mine", { params: { type: "holiday" } }).then((r) => setRequests(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmCancel = async () => {
    try {
      await api.post(`/leave/${cancelling.id}/cancel`);
      toast.success("Holiday cancelled");
      setCancelling(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to cancel");
    }
  };

  const canCancel = (r) =>
    (r.status === "pending" || r.status === "approved") && r.end_date >= todayStr();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight">My Leave</h1>
          <p className="text-sm mt-1" style={{ color: "#6A736D" }}>Request annual leave and track approvals.</p>
        </div>
        <RequestDialog open={open} setOpen={setOpen} onCreated={load} />
      </div>

      <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: "#EAE8E1" }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: "#F9F8F5" }}>
              {["Dates", "Days", "Note", "Status", "Submitted", ""].map((h) => (
                <th key={h} className="text-xs uppercase tracking-wider font-medium px-4 py-3 text-left" style={{ color: "#6A736D" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm" style={{ color: "#6A736D" }}>No leave requests yet.</td></tr>
            ) : requests.map((r) => (
              <tr key={r.id} className="border-b hover:bg-[#F4F3ED]/50 transition-colors" style={{ borderColor: "#EAE8E1" }} data-testid={`leave-row-${r.id}`}>
                <td className="px-4 py-3 text-sm font-medium">
                  {fmt(r.start_date)}{r.start_date !== r.end_date ? ` – ${fmt(r.end_date)}` : ""}
                  {r.duration === "half" && <span className="ml-2 text-xs" style={{ color: "#A35C3E" }}>½ day</span>}
                </td>
                <td className="px-4 py-3 text-sm">{r.days}</td>
                <td className="px-4 py-3 text-sm" style={{ color: "#6A736D" }}>{r.note || "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-sm" style={{ color: "#6A736D" }}>{fmt(r.created_at.slice(0, 10))}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button data-testid={`view-history-${r.id}`} onClick={() => setDetail(r)} className="text-sm flex items-center gap-1 hover:underline" style={{ color: "#647D6C" }}>
                      <History className="w-3.5 h-3.5" /> History
                    </button>
                    {canCancel(r) && (
                      <button data-testid={`cancel-leave-${r.id}`} onClick={() => setCancelling(r)} className="text-sm flex items-center gap-1 hover:underline" style={{ color: "#8C3B4A" }}>
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <HistoryDialog detail={detail} onClose={() => setDetail(null)} />

      <AlertDialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Cancel this holiday?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelling && (
                <>Cancelling {fmt(cancelling.start_date)}{cancelling.start_date !== cancelling.end_date ? ` – ${fmt(cancelling.end_date)}` : ""} ({cancelling.days} day{cancelling.days !== 1 ? "s" : ""}). {cancelling.status === "approved" ? "These days will be returned to your allowance." : "This request will be withdrawn."}</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-cancel-leave" onClick={confirmCancel} style={{ backgroundColor: "#8C3B4A" }}>
              Cancel holiday
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RequestDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({ start_date: "", end_date: "", duration: "full", note: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/leave", {
        type: "holiday",
        start_date: form.start_date,
        end_date: form.duration === "half" ? form.start_date : form.end_date,
        duration: form.duration,
        note: form.note,
      });
      toast.success("Leave request submitted");
      setOpen(false);
      setForm({ start_date: "", end_date: "", duration: "full", note: "" });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-1.5 w-full bg-white border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#647D6C]/20 focus:border-[#647D6C]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-leave-button" className="flex items-center gap-2 text-white rounded-md px-4 py-2 font-medium transition-colors" style={{ backgroundColor: "#647D6C" }}>
          <Plus className="w-4 h-4" /> Request leave
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Request annual leave</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium">Duration</label>
            <div className="mt-1.5 flex gap-2">
              {[{ k: "full", l: "Full day(s)" }, { k: "half", l: "Half day" }].map((o) => (
                <button
                  key={o.k}
                  data-testid={`duration-${o.k}`}
                  onClick={() => setForm({ ...form, duration: o.k })}
                  className={`flex-1 text-sm border rounded-md px-3 py-2 transition-colors ${form.duration === o.k ? "text-white" : "bg-white hover:bg-[#F4F3ED]"}`}
                  style={form.duration === o.k ? { backgroundColor: "#647D6C", borderColor: "#647D6C" } : { borderColor: "#EAE8E1" }}
                >{o.l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Start date</label>
              <input data-testid="leave-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} />
            </div>
            {form.duration === "full" && (
              <div>
                <label className="text-sm font-medium">End date</label>
                <input data-testid="leave-end-date" type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} />
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Note (optional)</label>
            <textarea data-testid="leave-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} placeholder="e.g. Family trip" />
          </div>
        </div>
        <DialogFooter>
          <button
            data-testid="submit-leave-button"
            onClick={submit}
            disabled={saving || !form.start_date || (form.duration === "full" && !form.end_date)}
            className="text-white rounded-md px-4 py-2 font-medium disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: "#647D6C" }}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Submit request
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HistoryDialog({ detail, onClose }) {
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Request history</DialogTitle></DialogHeader>
        {detail && (
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: "#6A736D" }}>{fmt(detail.start_date)}{detail.start_date !== detail.end_date ? ` – ${fmt(detail.end_date)}` : ""}</span>
              <StatusBadge status={detail.status} />
            </div>
            {detail.manager_comment && (
              <div className="text-sm border rounded-md p-3" style={{ borderColor: "#EAE8E1", backgroundColor: "#F9F8F5" }}>
                <span className="font-medium">Manager comment: </span>{detail.manager_comment}
              </div>
            )}
            <ol className="relative border-l pl-4 space-y-3" style={{ borderColor: "#EAE8E1" }}>
              {(detail.history || []).map((h, i) => (
                <li key={i} className="text-sm" data-testid={`history-item-${i}`}>
                  <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#647D6C" }} />
                  <div className="flex items-center gap-2">
                    <span className="font-medium capitalize">{h.action}</span>
                    <span className="text-xs" style={{ color: "#6A736D" }}>by {h.by}</span>
                  </div>
                  <span className="text-xs" style={{ color: "#6A736D" }}>{format(parseISO(h.at), "d MMM yyyy, HH:mm")}</span>
                  {h.comment && <p className="text-xs mt-0.5" style={{ color: "#6A736D" }}>“{h.comment}”</p>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
